import { RowEndosoMasivo } from "../models/rowEndosoMasivo";
import { format } from "rut.js";
import { columns } from "../../../../dtos/columns";
import { numericFormatter } from "react-number-format";
import Conversiones from "../../../../utils/conversiones";
import { MutuoSeleccionadoEndoso } from "../models/mutuoSeleccionadoEndoso";
import { Checkbox } from "@mui/material";
import { NumericFormat } from "react-number-format";
import { fechaRemesar } from "../services/endosoServices";

interface IProps {
    mutuosEndosar: MutuoSeleccionadoEndoso[];
    data: any,
    setMutuosEndosar: React.Dispatch<React.SetStateAction<MutuoSeleccionadoEndoso[]>>;
    fechaEndoso: Date;
    inversionista: string;
}

export function useColumns({mutuosEndosar, data, setMutuosEndosar, fechaEndoso, inversionista } : IProps) {

    const COLUMNS: columns<RowEndosoMasivo>[] = [
        {
            name: (
                <Checkbox
                    size="small"
                    checked={
                        data?.length > 0 &&
                        mutuosEndosar.length === data.length
                    }
                    onChange={(e) => {
                        if (e.target.checked && data) {
                            const allObjects = data.map((item: MutuoSeleccionadoEndoso) => ({
                                id: item.id,
                                numeroOperacion: item.numeroOperacion,
                                tasaEndoso: item.tasaEndoso,
                                numCuponesCortados: item.numCuponesCortados,
                                montoCredito: item.montoCredito,
                                tasaEmision: item.tasaEmision,
                                fechaRemesa: item.fechaRemesa
                            }));
                            setMutuosEndosar(allObjects);
                        } else {
                            setMutuosEndosar([]);
                        }
                    }}
                />
            ),
            selector: (row: RowEndosoMasivo) => row.id,
            cell: (row: RowEndosoMasivo) => (
                <Checkbox
                    size="small"
                    checked={mutuosEndosar.some((m) => m.id === row.id)}
                    onChange={(e) => {
                        setMutuosEndosar((prev) => {
                        if (e.target.checked) {
                            return [
                                ...prev,
                                {
                                    id: row.id,
                                    numeroOperacion: row.numeroOperacion,
                                    tasaEndoso: row.tasaEndoso,
                                    numCuponesCortados: row.numCuponesCortados,
                                    montoCredito: row.montoCredito,
                                    tasaEmision: row.tasaEmision,
                                    fechaRemesa: row.fechaRemesa
                                },
                            ];
                        } else {
                            return prev.filter((m) => m.id !== row.id);
                        }
                        });
                    }}
                />
            ),
            allowOverflow: true,
            button: true,
            export: false,
            width: '60px',
        },
        {
            name: 'N°',
            selector: (row: RowEndosoMasivo) => row.numero,
            sortable: true,
            sortField: 'numero',
            wrap: true,
            alling: 'left',
            export: true,
        }, 
        {
            name: 'N° operación',
            selector: (row: RowEndosoMasivo) => row.numeroOperacion,
            sortable: true,
            sortField: 'numeroOperacion',
            wrap: true,
            alling: 'left',
            export: true,
        },
        {
            name: 'RUT',
            selector: (row: RowEndosoMasivo) => format(row.rut!),
            sortable: true,
            sortField: 'rut',
            wrap: true,
            alling: 'left',
            export: true,
        }, 
        {
            name: 'Nombre cliente',
            selector: (row: RowEndosoMasivo) => row.nombreCliente,
            sortable: true,
            sortField: 'nombreCliente',
            wrap: true,
            alling: 'left',
            export: true,
        },
        {
            name: "Fecha firma primera escritura",
            selector: (row: RowEndosoMasivo) => Conversiones.fechaCortaFormat((row.fechaFirmaPrimeraEscritura ?? "").toString()),
            sortable: true,
            sortField: 'fechaFirmaPrimeraEscritura',
            wrap: true,
            export: true,
            alling: 'left',
        },
        {
            name: 'Plazo crédito',
            selector: (row: RowEndosoMasivo) => row.plazoCredito,
            sortable: true,
            sortField: 'plazoCredito',
            wrap: true,
            alling: 'left',
            export: true,
        }, 
        {
            name: 'Tasa emisión',
            selector: (row: RowEndosoMasivo) => row.tasaEmision,
            sortable: true,
            sortField: 'tasaEmision',
            wrap: true,
            alling: 'left',
            export: true,
        }, 
        {
            name: 'Tasa endoso',
            selector: (row: RowEndosoMasivo) => row.tasaEndoso, 
            cell: (row: RowEndosoMasivo) => {
                const currentValue = mutuosEndosar.find(m => m.id === row.id)?.tasaEndoso ?? row.tasaEndoso;

                return (
                    <NumericFormat 
                        value={currentValue}
                        className="form-control text-start"
                        decimalScale={4}
                        decimalSeparator=","
                        style={{ width: '80px' }}
                        autoComplete="off"
                        isAllowed={(values) => {                            
                            const { floatValue } = values;
                            return (
                                floatValue === undefined || floatValue >= 0
                            );
                        }}
                        onChange={(e) => {
                            const value = parseFloat(e.target.value.replace(",","."));
                            const validatedValue = !isNaN(value) ? value : 0
                            setMutuosEndosar(prev => {
                                const exists = prev.find(m => m.id === row.id);
                                if (exists) {
                                    return prev.map(m => m.id === row.id ? { ...m, tasaEndoso: validatedValue } : m);
                                } else {
                                    return [...prev, {
                                        id: row.id,
                                        numeroOperacion: row.numeroOperacion,
                                        tasaEndoso: validatedValue,
                                        numCuponesCortados: row.numCuponesCortados,
                                        montoCredito: row.montoCredito,
                                        tasaEmision: row.tasaEmision,
                                        fechaRemesa: row.fechaRemesa
                                    },];
                                }
                            });
                        }}
                    />
                );
            },
            sortable: true,
            sortField: 'tasaEndoso',
            wrap: true,
            alling: 'left',
            export: true
        },
        {
            name: 'N° cupones cortados',
            selector: (row: RowEndosoMasivo) => row.numCuponesCortados,
            cell: (row: RowEndosoMasivo) => {
                const currentValue = mutuosEndosar.find(m => m.id === row.id)?.numCuponesCortados ?? row.numCuponesCortados;

                return (
                    <NumericFormat 
                        value={currentValue}
                        className="form-control text-start"
                        decimalScale={0}
                        decimalSeparator=","
                        style={{ width: '80px' }}
                        autoComplete="off"
                        isAllowed={(values) => {                            
                            const { floatValue } = values;
                            return (
                                floatValue === undefined || floatValue >= 0
                            );
                        }}
                        onChange={(e) => {
                            const value = parseFloat(e.target.value);
                            const validatedValue = !isNaN(value) ? value : 0
                            setMutuosEndosar(prev => {
                                const exists = prev.find(m => m.id === row.id);
                                if (exists) {
                                    return prev.map(m => m.id === row.id ? { ...m, numCuponesCortados: validatedValue } : m);
                                } else {
                                    return [...prev, {
                                        id: row.id,
                                        numeroOperacion: row.numeroOperacion,
                                        tasaEndoso: row.tasaEndoso,
                                        numCuponesCortados: validatedValue,
                                        montoCredito: row.montoCredito,
                                        tasaEmision: row.tasaEmision,
                                        fechaRemesa: row.fechaRemesa
                                    },];
                                }
                            });
                        }}
                        onFocus={(e) => {
                            e.target.dataset.initialValue = e.target.value;
                        }}
                        onBlur={async (e) => {
                            if (e.target.dataset.initialValue === e.target.value || !inversionista) {
                                return;
                            }

                            const mutuoEditado = mutuosEndosar.find((m) => m.id === row.id);
                            const numCuponesCortados = mutuoEditado?.numCuponesCortados ?? row.numCuponesCortados;

                            try {
                                const response = await fechaRemesar(row.id, numCuponesCortados, inversionista, fechaEndoso);
                                const nuevaFechaRemesa = response.data.fechaRemesa;

                                setMutuosEndosar((prev) => {
                                    const exists = prev.find((m) => m.id === row.id);
                                    if (exists) {
                                        return prev.map((m) => m.id === row.id ? { ...m, fechaRemesa: nuevaFechaRemesa } : m);
                                    }

                                    return [...prev, {
                                        id: row.id,
                                        numeroOperacion: row.numeroOperacion,
                                        tasaEndoso: row.tasaEndoso,
                                        numCuponesCortados,
                                        montoCredito: row.montoCredito,
                                        tasaEmision: row.tasaEmision,
                                        fechaRemesa: nuevaFechaRemesa
                                    }];
                                });
                            } catch (error) {
                                console.error(error);
                            }
                        }}
                    />
                );
            },
            sortable: true,
            sortField: 'numCuponesCortados',
            wrap: true,
            alling: 'left',
            export: true
        },
        {
            name: "Fecha a remesar",
            selector: (row: RowEndosoMasivo) => {
                const fechaRemesaActual = mutuosEndosar.find((m) => m.id === row.id)?.fechaRemesa ?? row.fechaRemesa;
                return Conversiones.fechaCortaFormat((fechaRemesaActual ?? "").toString());
            },
            sortable: true,
            sortField: 'fechaRemesa',
            wrap: true,
            export: true,
            alling: 'left',
        },
        {
            name: 'Meses de gracia',
            selector: (row: RowEndosoMasivo) => row.mesesGracia,
            sortable: true,
            sortField: 'mesesGracia',
            wrap: true,
            alling: 'left',
            export: true,
        }, 
        {
            name: 'N° dividendos impagos',
            selector: (row: RowEndosoMasivo) => row.numDividendosImpagos,
            sortable: true,
            sortField: 'numDividendosImpagos',
            wrap: true,
            alling: 'left',
            export: true,
        }, 
        {
            name: 'Monto crédito',
            selector: (row: RowEndosoMasivo) => numericFormatter(row.montoCredito.toFixed(4), { "thousandsGroupStyle": "thousand", "thousandSeparator": ".", "decimalSeparator": ",", "prefix": "UF " }),
            sortable: true,
            sortField: 'montoCredito',
            wrap: true,
            alling: 'left',
            export: true,
        }, 
        {
            name: 'Valor garantía',
            selector: (row: RowEndosoMasivo) => numericFormatter(row.valorGarantia.toFixed(4), { "thousandsGroupStyle": "thousand", "thousandSeparator": ".", "decimalSeparator": ",", "prefix": "UF " }),
            sortable: true,
            sortField: 'valorGarantia',
            wrap: true,
            alling: 'left',
            export: true,
        }, 
        {
            name: 'Comuna CBR',
            selector: (row: RowEndosoMasivo) => row.comunaCBR,
            sortable: true,
            sortField: 'comunaCBR',
            wrap: true,
            alling: 'left',
            export: true,
        }, 
        {
            name: 'LTV%',
            selector: (row: RowEndosoMasivo) => row.ltv,
            sortable: true,
            sortField: 'ltv',
            wrap: true,
            alling: 'left',
            export: true,
        }
    ]

    return {COLUMNS};
}